import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { ShipBuyerPacketToSellerResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to ship buyer packet to seller
 */
export const shipBuyerPacketToSeller = async (
	servicesProviderId: string,
	orderId: string,
): Promise<ShipBuyerPacketToSellerResponse> => {
	const response = await apiClient.post<ShipBuyerPacketToSellerResponse>(
		ordersEndpoints.shipBuyerPacketToSeller(servicesProviderId, orderId),
		{},
	);
	return response.data;
};

/**
 * Hook for shipping buyer packet to seller
 * Generates a UPS shipping label for the seller packet
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useShipBuyerPacketToSeller = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to ship buyer packet to seller without an order ID or services provider ID.',
				);
			}

			return shipBuyerPacketToSeller(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.SHIP_BUYER_PACKET_TO_SELLER);
		},
	});
};
